import { fetchWithReadRetry, retryAfterMs } from './readRetry';
import { loginError, type SessionReplacement } from './sessionLimit';
import type {
  Board,
  BoardStatus,
  Job,
  Pipeline,
  PipelineStage,
  Opportunity,
  OpportunityDetail,
  Contract,
  ContractFormData,
  ContractTemplate,
  Communication,
  CommunicationFormData,
  Activity,
  ActivityFormData,
  Contact,
  ESignatureSession,
  ESignatureSessionFormData,
  SignatureSubmissionData,
  ContractPreviewData,
  OfflineSignature,
  Document as DocType,
  Tag,
  User
} from '../types';
import type { ContactSource } from '../types/crm';
import type {
  SupportTicket,
  TicketMessage,
  CreateTicketPayload,
  CreateMessagePayload,
  AIChatMessage,
  AIConversation,
} from '../types/support';
import type {
  EmailTemplate,
  EmailCampaign,
  EmailCampaignStats,
  DripCampaign,
  DripEnrollment,
  EmailQuota,
  WebsitePagesForImportResponse,
  ImportFromWebsiteResponse,
} from '../types/marketing';
import type {
  EditorDocumentResponse,
  VisualEditPayload,
  VisualEditResponse,
  DnsRecord,
  ReadinessResponse,
  WebsiteAiEditDispatchResponse,
  WebsiteComponentOptions,
  WebsitePageDetail,
  WebsitePageSummary,
  WebsitePageCreatePayload,
  WebsitePageCreateResponse,
  WebsitePageUpdatePayload,
  WebsitePageUpdateResponse,
  WebsiteProject,
  WebsiteProjectCreatePayload,
  WebsiteProjectDetail,
  WebsiteProjectStatus,
  WebsiteProjectUpdatePayload,
  WebsiteRevisionInfo,
  WebsiteStyleOptions,
  WebsiteTaskDispatchResponse,
} from '../types/websiteBuilder';
import type {
  BackfillMode,
  QuickBooksConnectResponse,
  QuickBooksDisconnectResponse,
  QuickBooksLogFilter,
  QuickBooksReferenceData,
  QuickBooksRetryResponse,
  QuickBooksStatus,
  QuickBooksSyncLog,
  QuickBooksSyncNowResponse,
  QuickBooksSyncSettings,
  QuickBooksSyncSettingsUpdate,
} from '../types/quickbooks';
import { authService } from './auth';
import { getImpersonatedUserId } from './impersonation';
// REMOVED: apiInterceptor - per-seat billing has no usage limits

// Determine API base URL dynamically
const getApiBaseUrl = () => {
  // Always use same-origin for API requests
  // The React app should be served from the same domain as the API
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();


export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

export interface PhotoEmailRecipient {
  email: string;
  name: string;
  role: string;
}

export interface PhotoEmailRecipients {
  to: PhotoEmailRecipient[];
  cc_suggestions: PhotoEmailRecipient[];
  /** Contacts deliberately left out — no address, or opted out of email. */
  blocked: (PhotoEmailRecipient & { reason: string })[];
}

export interface KanbanData {
  boards: Board[];
  statuses: BoardStatus[];
  jobs: Job[];
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { isFormData?: boolean } = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get JWT access token
    const accessToken = authService.getAccessToken();

    // Super Admin impersonation: backend only honors this header when the
    // JWT belongs to a superuser, then scopes the request to the target user.
    const impersonatedUserId = getImpersonatedUserId();

    // Don't set Content-Type for FormData - let browser set it with boundary
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...(impersonatedUserId && { 'X-Impersonate-User-Id': String(impersonatedUserId) }),
      ...(options.headers as Record<string, string>),
    };
    
    // Only add Content-Type for non-FormData requests
    if (!options.isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    const config: RequestInit = {
      // Include credentials for cookie-based auth (refresh token cookie)
      credentials: 'include',
      ...options,
      headers,
    };

    try {
      let response;
      try {
        response = await fetchWithReadRetry(url, config);
      } catch (fetchError) {
        // Safari-specific error handling for SSL/TLS issues
        
        // Check if this is Safari
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          // For Safari, try to determine if user is still authenticated
          // by checking if we can access the sessionid cookie
          const getCookie = (name: string) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop()?.split(';').shift();
            return null;
          };
          
          const sessionId = getCookie('backend_sessionid') || getCookie('sessionid');
          if (sessionId) {
            // Don't redirect to login if we have a session cookie
            throw new Error('Network request failed - please try refreshing the page or using a different browser');
          }
        }
        
        // Re-throw the error for normal handling
        throw fetchError;
      }
      
      if (!response.ok) {
        
        // Handle authentication failures
        if (response.status === 401) {
          // Try to refresh token first (cross-tab lock + retries live in AuthService).
          // If that fails, give initializeAuth one more chance — a peer tab may
          // have already rotated the cookie while we lost the race.
          const retryWithAccess = async (accessToken: string) => {
            headers['Authorization'] = `Bearer ${accessToken}`;
            const retryResponse = await fetchWithReadRetry(url, { ...config, headers });
            if (!retryResponse.ok) {
              return null;
            }
            const data = await retryResponse.json();
            return {
              data: data,
              status: retryResponse.status,
              message: data.message,
            } as { data: T; status: number; message?: string };
          };

          try {
            const newAccessToken = await authService.refreshAccessToken();
            const recovered = await retryWithAccess(newAccessToken);
            if (recovered) {
              return recovered;
            }
          } catch {
            try {
              const recoveredSession = await authService.initializeAuth();
              if (recoveredSession) {
                const token = authService.getAccessToken();
                if (token) {
                  const recovered = await retryWithAccess(token);
                  if (recovered) {
                    return recovered;
                  }
                }
              }
            } catch {
              // Fall through to logout redirect
            }
            authService.clearAccessToken();
          }

          // Preserve the current path for post-login redirect
          const currentPath = window.location.pathname + window.location.search;
          const loginUrl = `/login/?next=${encodeURIComponent(currentPath)}`;
          window.location.href = loginUrl;
          return { data: null as T, status: response.status };
        }
        
        // Handle 403 Forbidden errors
        if (response.status === 403) {
          // Try to get error details from response
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            // Could not parse 403 error response
          }

          if (typeof errorData?.error === 'string' && errorData.error.includes('subscription is inactive')) {
            // Background requests can be billing-blocked even while the user
            // is fixing their subscription. Redirecting this page to itself
            // reloads the app and starts those same requests again.
            const onBillingPage = window.location.pathname === '/settings/billing' ||
              window.location.pathname.startsWith('/settings/billing/');
            if (!onBillingPage) {
              window.location.href = errorData.redirect_url || '/settings/billing';
            }
            const error = new Error(errorData.error);
            (error as any).responseData = errorData;
            (error as any).status = 403;
            throw error;
          }

          // Permission failures are not expired sessions.
          if (typeof errorData?.detail === 'string' &&
              (errorData.detail.includes('organization admin') ||
               errorData.detail.includes('permission') ||
               errorData.detail.includes('not allowed'))) {
            const error = new Error(errorData.detail);
            (error as any).responseData = errorData;
            (error as any).status = 403;
            throw error;
          }

          // Only redirect to login if we're sure it's an authentication issue
          // Check if we still have a valid token
          if (authService.isAuthenticated()) {
            const error = new Error('Permission denied');
            (error as any).responseData = errorData;
            (error as any).status = 403;
            throw error;
          }

          // Only redirect if no valid token
          authService.clearAccessToken();
          const currentPath = window.location.pathname + window.location.search;
          const loginUrl = `/login/?next=${encodeURIComponent(currentPath)}`;
          window.location.href = loginUrl;
          return { data: null as T, status: response.status };
        }
        
        // Try to get error details from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // Could not parse error response
        }
        
        const error = new Error(`HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).responseData = errorData;
        throw error;
      }
      
      // Handle empty responses (like 204 No Content from DELETE)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {
          data: null as T,
          status: response.status,
        };
      }

      const data = response.headers.get('content-type')?.includes('text/csv') ? await response.text() : await response.json();

      return {
        data: data,
        status: response.status,
        message: data.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: any, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    // Check if data is FormData
    const isFormData = data instanceof FormData;
    
    return this.request<T>(endpoint, {
      method: 'POST',
      ...(headers && { headers }),
      body: isFormData ? data : JSON.stringify(data),
      ...(isFormData && { isFormData: true } as any),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    // Check if data is FormData
    const isFormData = data instanceof FormData;

    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: isFormData ? data : JSON.stringify(data),
      ...(isFormData && { isFormData: true } as any),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Kanban-specific methods
  async getBoards(): Promise<ApiResponse<Board[]>> {
    return this.getAllPages<Board>('/api/boards/');
  }

  async getBoardStatuses(boardId: number): Promise<ApiResponse<BoardStatus[]>> {
    return this.getAllPages<BoardStatus>(`/api/board-statuses/by-board/${boardId}/`);
  }

  async getTags(): Promise<ApiResponse<Tag[]>> {
    const response = await this.get<Tag[] | { count: number; results: Tag[] }>('/api/tags/?page_size=200');
    // Handle both direct array and paginated responses
    const tags = Array.isArray(response.data)
      ? response.data
      : (response.data && 'results' in response.data ? response.data.results : []);
    return { ...response, data: tags };
  }

  async createTag(name: string, color: string): Promise<ApiResponse<Tag>> {
    return this.post<Tag>('/api/tags/', { name, color });
  }

  async updateTag(id: number, data: { name?: string; color?: string }): Promise<ApiResponse<Tag>> {
    return this.patch<Tag>(`/api/tags/${id}/`, data);
  }

  async deleteTag(id: number): Promise<ApiResponse<void>> {
    // Soft delete - sets is_deleted=true
    return this.patch<void>(`/api/tags/${id}/`, { is_deleted: true });
  }

  async createContactSource(name: string): Promise<ApiResponse<ContactSource>> {
    return this.post<ContactSource>('/api/crm/contact-sources/', { name });
  }

  async getUsers(): Promise<ApiResponse<User[]>> {
    const response = await this.get<User[] | { count: number; results: User[] }>('/api/users/');
    // Handle both direct array and paginated responses
    const users = Array.isArray(response.data)
      ? response.data
      : (response.data && 'results' in response.data ? response.data.results : []);
    return { ...response, data: users };
  }

  async getJobs(
    boardId?: number,
    includeCompleted?: boolean,
    pageSize?: number,
  ): Promise<ApiResponse<Job[]>> {
    const params = new URLSearchParams();
    if (boardId) params.append('board', String(boardId));
    if (includeCompleted) params.append('include_completed', 'true');
    if (pageSize) params.append('page_size', String(pageSize));
    const endpoint = `/api/jobs/${params.toString() ? `?${params.toString()}` : ''}`;
    return this.getAllPages<Job>(endpoint);
  }

  async updateJobStatus(jobId: number, statusId: number): Promise<ApiResponse<Job>> {
    return this.patch<Job>(`/api/jobs/${jobId}/`, {
      board_status: statusId,
    });
  }

  async markJobComplete(jobId: number, isCompleted: boolean = true): Promise<ApiResponse<Job>> {
    return this.post<Job>(`/api/jobs/${jobId}/mark-complete/`, {
      is_completed: isCompleted,
    });
  }

  async createJob(job: Partial<Job>): Promise<ApiResponse<Job>> {
    return this.post<Job>('/api/jobs/', job);
  }

  async updateJob(jobId: number, job: Partial<Job>): Promise<ApiResponse<Job>> {
    return this.patch<Job>(`/api/jobs/${jobId}/`, job);
  }

  async deleteJob(jobId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/jobs/${jobId}/`);
  }

  // Photo upload method
  async uploadPhotos(jobId: number, files: File[], description?: string): Promise<ApiResponse<any>> {
    const formData = new FormData();
    
    // Add job ID
    formData.append('job', jobId.toString());
    
    // Add description if provided
    if (description) {
      formData.append('description', description);
    }
    
    // Add all image files with the same key name 'image'
    files.forEach((file) => {
      formData.append('image', file);
    });

    // Get JWT access token
    const accessToken = authService.getAccessToken();
    
    const url = `${this.baseUrl}/api/photos/`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(`HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).responseData = errorData;
        throw error;
      }

      const data = await response.json();

      return {
        data,
        status: response.status,
        message: data.message,
      };
    } catch (error) {
      throw error;
    }
  }

  // Presigned URL Upload Methods — files go directly to S3, zero web dyno memory

  /**
   * Get a presigned S3 URL for direct upload. Creates a Photo record in pending state.
   */
  async getPresignedUploadUrl(params: {
    jobId?: number | null;
    opportunityId?: number | null;
    checklistStepRunId?: number;
    batchId?: number;
    filename: string;
    contentType: string;
    description?: string;
  }): Promise<ApiResponse<{
    photo_id: number;
    presigned_url: string;
    temp_key: string;
    expires_in: number;
  }>> {
    return this.post('/api/photos/presign/', {
      job: params.jobId || null,
      opportunity: params.opportunityId || null,
      checklist_step_run: params.checklistStepRunId || null,
      batch_id: params.batchId || null,
      filename: params.filename,
      content_type: params.contentType,
      description: params.description || '',
    });
  }

  /**
   * Upload a file directly to S3 using a presigned URL.
   * This bypasses the web dyno entirely — no memory pressure on the server.
   */
  async uploadFileToPresignedUrl(presignedUrl: string, file: File): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Confirm that a presigned URL upload completed. Triggers server-side processing.
   */
  async confirmPhotoUpload(photoId: number, tags?: number[] | string): Promise<ApiResponse<{
    photo: any;
    upload_status: string;
    message: string;
  }>> {
    return this.post('/api/photos/confirm-upload/', {
      photo_id: photoId,
      tags: tags || [],
    });
  }

  // Photo Batch Upload Methods (for uploading multiple photos without 413 errors)

  /**
   * Create a photo batch to track upload progress.
   * Use this when uploading multiple photos to avoid payload size limits.
   */
  async createPhotoBatch(
    jobId: number | null,
    opportunityId: number | null,
    totalPhotos: number,
    metadata?: { description?: string; tags?: string }
  ): Promise<ApiResponse<{
    id: number;
    total_photos: number;
    uploaded_photos: number;
    failed_photos: number;
    status: string;
  }>> {
    return this.post('/api/photo-batches/', {
      job: jobId,
      opportunity: opportunityId,
      total_photos: totalPhotos,
      metadata: metadata || {},
    });
  }

  /**
   * Upload a single photo to an existing batch.
   * Returns progress information after each upload.
   */
  async uploadSinglePhotoToBatch(
    batchId: number,
    file: File,
    options?: {
      description?: string;
      checklistStepRunId?: number;
    }
  ): Promise<ApiResponse<{
    photo: any;
    batch_progress: {
      uploaded: number;
      total: number;
      failed: number;
      is_complete: boolean;
      progress_percent: number;
    };
  }>> {
    const formData = new FormData();
    formData.append('batch_id', batchId.toString());
    formData.append('image', file);
    if (options?.description) {
      formData.append('description', options.description);
    }
    if (options?.checklistStepRunId) {
      formData.append('checklist_step_run', options.checklistStepRunId.toString());
    }

    const accessToken = authService.getAccessToken();
    const url = `${this.baseUrl}/api/photo-batches/upload-single/`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      },
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const error = new Error(errorData?.error || `HTTP error! status: ${response.status}`);
      (error as any).responseData = errorData;
      throw error;
    }

    const data = await response.json();
    return {
      data,
      status: response.status,
      message: data.message,
    };
  }

  /**
   * Get the status of a photo batch.
   */
  async getPhotoBatchStatus(batchId: number): Promise<ApiResponse<{
    id: number;
    total_photos: number;
    uploaded_photos: number;
    failed_photos: number;
    status: string;
    progress_percent: number;
  }>> {
    return this.get(`/api/photo-batches/${batchId}/`);
  }

  /**
   * Persist the gallery order for one job or opportunity.
   * `photo_ids` must be the complete order, first to last.
   */
  async reorderPhotos(params: {
    photo_ids: number[];
    opportunity?: number;
    job?: number;
  }): Promise<ApiResponse<{ reordered: number }>> {
    return this.post('/api/photos/reorder/', params);
  }

  /** Addresses to prefill an email with for a job or opportunity. */
  async getPhotoEmailRecipients(params: {
    opportunity?: number;
    job?: number;
  }): Promise<ApiResponse<PhotoEmailRecipients>> {
    const query = params.opportunity
      ? `opportunity=${params.opportunity}`
      : `job=${params.job}`;
    return this.get(`/api/photos/email-recipients/?${query}`);
  }

  /**
   * Email a selection of photos. The server resizes and attaches them on a
   * worker, so this returns 202 as soon as the send is queued.
   */
  async emailPhotos(params: {
    photo_ids: number[];
    opportunity?: number;
    job?: number;
    to?: string[];
    cc?: string[];
    subject?: string;
    message?: string;
    report_id?: string;
  }): Promise<ApiResponse<{ queued: boolean; photo_count: number; to: string[]; cc: string[]; message: string }>> {
    return this.post('/api/photos/email/', params);
  }

  /**
   * Follow a paginated list endpoint to the end and return one flat array.
   *
   * Asking for `?page_size=1000` does not work and fails silently, which is
   * the trap this exists to close. DRF clamps the value to the view's
   * `max_page_size` (100 on photos), and where a view uses the project
   * default paginator the `page_size` parameter is not wired up at all, so the
   * request quietly falls back to `PAGE_SIZE = 20`. In both cases the response
   * looks perfectly healthy and simply omits the rest of the rows.
   *
   * Callers get every row; each request to the server stays bounded. The page
   * cap is a backstop against an endpoint whose `next` never goes null — it
   * bounds worst-case memory here to `maxPages * pageSize` rows rather than
   * letting a server-side bug spin the browser.
   *
   * Also tolerates the older non-paginated (bare array) response shape.
   */
  async getAllPages<T = any>(
    path: string,
    options: { pageSize?: number; maxPages?: number } = {}
  ): Promise<ApiResponse<T[]>> {
    const { pageSize = 100, maxPages = 100 } = options;
    const separator = path.includes('?') ? '&' : '?';
    const all: T[] = [];
    let page = 1;
    let lastStatus = 200;

    for (let i = 0; i < maxPages; i++) {
      const response = await this.get<any>(
        `${path}${separator}page=${page}&page_size=${pageSize}`
      );
      lastStatus = response.status;
      const data = response.data;

      if (Array.isArray(data)) {
        // Non-paginated (older backend) — everything in one response.
        all.push(...data);
        break;
      }
      if (data && Array.isArray(data.results)) {
        all.push(...data.results);
        if (!data.next) break;
        page += 1;
        continue;
      }
      break;
    }

    return { data: all, status: lastStatus };
  }

  // Opportunity Photos
  async getOpportunityPhotos(opportunityId: number): Promise<ApiResponse<any[]>> {
    return this.getAllPages(`/api/photos/by-opportunity/${opportunityId}/`);
  }

  async uploadOpportunityPhotos(opportunityId: number, files: File[], description?: string): Promise<ApiResponse<any>> {
    const formData = new FormData();
    
    // Add opportunity ID
    formData.append('opportunity', opportunityId.toString());
    
    // Add description if provided
    if (description) {
      formData.append('description', description);
    }
    
    // Add all image files with the same key name 'image'
    files.forEach((file) => {
      formData.append('image', file);
    });

    // Get JWT access token
    const accessToken = authService.getAccessToken();
    
    const url = `${this.baseUrl}/api/photos/`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(`HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).responseData = errorData;
        throw error;
      }

      const data = await response.json();

      return {
        data,
        status: response.status,
        message: data.message,
      };
    } catch (error) {
      throw error;
    }
  }

  // Opportunity Documents
  // /api/docs/ uses the project-default paginator, which ignores `page_size`
  // entirely — the old `?page_size=1000` here returned exactly 20 documents.
  async getOpportunityDocs(opportunityId: number): Promise<ApiResponse<DocType[]>> {
    return this.getAllPages<DocType>(`/api/docs/?opportunity=${opportunityId}`);
  }

  async uploadOpportunityDocs(opportunityId: number, file: File, options?: { measurement?: boolean }): Promise<ApiResponse<any>> {
    const formData = new FormData();

    // Add opportunity ID
    formData.append('opportunity', opportunityId.toString());

    // Add single file
    formData.append('file', file);

    // Add measurement flag if provided
    if (options?.measurement !== undefined) {
      formData.append('measurement', options.measurement.toString());
    }

    // Get JWT access token
    const accessToken = authService.getAccessToken();
    
    const url = `${this.baseUrl}/api/docs/`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(`HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).responseData = errorData;
        throw error;
      }

      const data = await response.json();

      return {
        data,
        status: response.status,
        message: data.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async deleteDoc(docId: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/docs/${docId}/`);
  }
  
  async updateDoc(docId: number, data: { tag_names?: string }): Promise<ApiResponse<DocType>> {
    return this.patch<DocType>(`/api/docs/${docId}/`, data);
  }

  // Kanban data loader
  async getKanbanData(boardId: number, includeCompleted?: boolean): Promise<KanbanData> {
    const [boardsResponse, statusesResponse, jobsResponse] = await Promise.all([
      this.getBoards(),
      this.getBoardStatuses(boardId),
      this.getJobs(boardId, includeCompleted),
    ]);

    return {
      boards: boardsResponse.data,
      statuses: statusesResponse.data,
      jobs: jobsResponse.data,
    };
  }

  // CRM Pipeline Methods
  async getPipelines(): Promise<ApiResponse<Pipeline[]>> {
    return this.get<Pipeline[]>('/api/pipelines/');
  }

  async getPipelineStages(pipelineId: number): Promise<ApiResponse<PipelineStage[]>> {
    return this.get<PipelineStage[]>(`/api/pipelines/${pipelineId}/stages/`);
  }

  async getOpportunities(pipelineId?: number): Promise<ApiResponse<Opportunity[]>> {
    const endpoint = pipelineId ? `/api/crm/opportunities/?pipeline=${pipelineId}` : '/api/crm/opportunities/';
    const response = await this.get<Opportunity[] | { count: number; results: Opportunity[] }>(endpoint);
    // Handle both direct array and paginated responses
    const opportunities = Array.isArray(response.data) 
      ? response.data 
      : (response.data && 'results' in response.data ? response.data.results : []);
    return { ...response, data: opportunities };
  }

  async getOpportunityDetail(opportunityId: number): Promise<ApiResponse<OpportunityDetail>> {
    return this.get<OpportunityDetail>(`/api/crm/opportunities/${opportunityId}/`);
  }

  async getOpportunity(opportunityId: number | string): Promise<ApiResponse<OpportunityDetail>> {
    return this.get<OpportunityDetail>(`/api/crm/opportunities/${opportunityId}/`);
  }

  async createOpportunity(opportunity: any): Promise<ApiResponse<Opportunity>> {
    return this.post<Opportunity>('/api/crm/opportunities/', opportunity);
  }

  async updateOpportunity(opportunityId: number, opportunity: any): Promise<ApiResponse<Opportunity>> {
    return this.patch<Opportunity>(`/api/crm/opportunities/${opportunityId}/`, opportunity);
  }

  async updateOpportunityStage(opportunityId: number, stageId: number): Promise<ApiResponse<Opportunity>> {
    return this.patch<Opportunity>(`/api/crm/opportunities/${opportunityId}/`, {
      stage: stageId,
    });
  }

  async convertOpportunityToJob(opportunityId: number, boardId: number, statusId: number, name?: string): Promise<ApiResponse<{ success: boolean; job_id: number; message: string }>> {
    return this.post(`/api/crm/opportunities/${opportunityId}/convert-to-job/`, {
      board_id: boardId,
      status_id: statusId,
      name,
    });
  }

  async deleteOpportunity(opportunityId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/crm/opportunities/${opportunityId}/`);
  }

  // Contract Methods
  async getContracts(): Promise<ApiResponse<Contract[]>> {
    return this.get<Contract[]>('/api/contracts/');
  }

  async getContract(contractId: number): Promise<ApiResponse<Contract>> {
    return this.get<Contract>(`/api/contracts/${contractId}/`);
  }

  async createContract(contract: ContractFormData): Promise<ApiResponse<Contract>> {
    return this.post<Contract>('/api/contracts/', contract);
  }

  async updateContract(contractId: number, contract: Partial<ContractFormData>): Promise<ApiResponse<Contract>> {
    return this.patch<Contract>(`/api/contracts/${contractId}/`, contract);
  }

  async deleteContract(contractId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/contracts/${contractId}/`);
  }

  // CRM Contract Methods (using /api/crm/contracts/ endpoints)
  async getCrmContracts(): Promise<ApiResponse<Contract[]>> {
    return this.get<Contract[]>('/api/crm/contracts/');
  }

  async getCrmContract(contractId: number | string): Promise<ApiResponse<Contract>> {
    return this.get<Contract>(`/api/crm/contracts/${contractId}/`);
  }

  async createCrmContract(contract: ContractFormData): Promise<ApiResponse<Contract>> {
    return this.post<Contract>('/api/crm/contracts/', contract);
  }

  async updateCrmContract(contractId: number | string, contract: Partial<ContractFormData>): Promise<ApiResponse<Contract>> {
    return this.patch<Contract>(`/api/crm/contracts/${contractId}/`, contract);
  }

  async deleteCrmContract(contractId: number | string): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/crm/contracts/${contractId}/`);
  }

  async sendCrmContract(contractId: number | string, data?: any): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/crm/contracts/${contractId}/send/`, data || {});
  }

  async sendCrmContractCopy(contractId: number | string, data: { email?: string; message?: string }): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/crm/contracts/${contractId}/send-copy/`, data);
  }

  async archiveCrmContract(contractId: number | string): Promise<ApiResponse<Contract>> {
    return this.post<Contract>(`/api/crm/contracts/${contractId}/archive/`, {});
  }

  async unarchiveCrmContract(contractId: number | string): Promise<ApiResponse<Contract>> {
    return this.post<Contract>(`/api/crm/contracts/${contractId}/unarchive/`, {});
  }

  async uploadCrmContractPdf(contractId: number | string, file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('pdf_file', file);
    return this.post<any>(`/api/crm/contracts/${contractId}/upload_pdf/`, formData);
  }

  async uploadContractTemplatePdf(templateId: number | string, file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('pdf_file', file);
    return this.post<any>(`/api/crm/contract-templates/${templateId}/upload_pdf/`, formData);
  }

  async createCrmESignatureSession(sessionData: any): Promise<ApiResponse<any>> {
    return this.post<any>('/api/crm/esignature-sessions/', sessionData);
  }

  // Contract Template Methods
  async getContractTemplates(): Promise<ApiResponse<ContractTemplate[]>> {
    return this.get<ContractTemplate[]>('/api/contract-templates/');
  }

  async getContractTemplate(templateId: number): Promise<ApiResponse<ContractTemplate>> {
    return this.get<ContractTemplate>(`/api/contract-templates/${templateId}/`);
  }

  async createContractTemplate(template: Partial<ContractTemplate>): Promise<ApiResponse<ContractTemplate>> {
    return this.post<ContractTemplate>('/api/contract-templates/', template);
  }

  async updateContractTemplate(templateId: number, template: Partial<ContractTemplate>): Promise<ApiResponse<ContractTemplate>> {
    return this.patch<ContractTemplate>(`/api/contract-templates/${templateId}/`, template);
  }

  async deleteContractTemplate(templateId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/contract-templates/${templateId}/`);
  }

  // Communication Methods
  async getCommunications(opportunityId?: number, jobId?: number): Promise<ApiResponse<Communication[]>> {
    let endpoint = '/api/communications/';
    const params = new URLSearchParams();
    
    if (opportunityId) params.append('related_opportunity', opportunityId.toString());
    if (jobId) params.append('related_job', jobId.toString());
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    return this.get<Communication[]>(endpoint);
  }

  async getCommunication(communicationId: number): Promise<ApiResponse<Communication>> {
    return this.get<Communication>(`/api/communications/${communicationId}/`);
  }

  async createCommunication(communication: CommunicationFormData): Promise<ApiResponse<Communication>> {
    return this.post<Communication>('/api/communications/', communication);
  }

  async updateCommunication(communicationId: number, communication: Partial<CommunicationFormData>): Promise<ApiResponse<Communication>> {
    return this.patch<Communication>(`/api/communications/${communicationId}/`, communication);
  }

  async deleteCommunication(communicationId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/communications/${communicationId}/`);
  }

  // Activity Methods
  async getActivities(opportunityId?: number, contactId?: number): Promise<ApiResponse<Activity[]>> {
    let endpoint = '/api/activities/';
    const params = new URLSearchParams();
    
    if (opportunityId) params.append('opportunity', opportunityId.toString());
    if (contactId) params.append('contact', contactId.toString());
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    return this.get<Activity[]>(endpoint);
  }

  async getActivity(activityId: number): Promise<ApiResponse<Activity>> {
    return this.get<Activity>(`/api/activities/${activityId}/`);
  }

  async createActivity(activity: ActivityFormData): Promise<ApiResponse<Activity>> {
    return this.post<Activity>('/api/activities/', activity);
  }

  async updateActivity(activityId: number, activity: Partial<ActivityFormData>): Promise<ApiResponse<Activity>> {
    return this.patch<Activity>(`/api/activities/${activityId}/`, activity);
  }

  async deleteActivity(activityId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/activities/${activityId}/`);
  }

  // User Profile Methods
  async getCurrentUser(): Promise<ApiResponse<any>> {
    return this.get<any>('/api/users/current/');
  }

  async updateCurrentUser(data: { email?: string; first_name?: string; last_name?: string; mobile_phone?: string }): Promise<ApiResponse<any>> {
    return this.patch<any>('/api/users/current/', data);
  }

  async changePassword(data: { current_password: string; new_password: string; confirm_password: string }): Promise<ApiResponse<any>> {
    return this.post<any>('/api/users/change-password/', data);
  }

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse<any>> {
    return this.post<any>('/api/users/reset-password/', {
      token,
      new_password: newPassword
    });
  }

  // Contact Methods (Enhanced for CRM)
  async getContacts(filters?: { 
    contact_type?: string;
    page_size?: number;
    search?: string;
  }): Promise<ApiResponse<any>> {
    let endpoint = '/api/crm/contacts/';
    const params = new URLSearchParams();
    
    if (filters?.contact_type) {
      params.append('contact_type', filters.contact_type);
    }
    if (filters?.page_size) {
      params.append('page_size', filters.page_size.toString());
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    
    const queryString = params.toString();
    if (queryString) {
      endpoint += `?${queryString}`;
    }
    
    const response = await this.get<any[] | { count: number; results: any[] }>(endpoint);
    // Handle both direct array and paginated responses
    const contacts = Array.isArray(response.data)
      ? response.data
      : (response.data && 'results' in response.data ? response.data.results : []);
    return { ...response, data: contacts };
  }

  async getContact(contactId: number): Promise<ApiResponse<Contact>> {
    return this.get<Contact>(`/api/crm/contacts/${contactId}/`);
  }

  async createContact(contact: Partial<Contact>): Promise<ApiResponse<Contact>> {
    return this.post<Contact>('/api/crm/contacts/', contact);
  }

  async updateContact(contactId: number, contact: Partial<Contact>): Promise<ApiResponse<Contact>> {
    return this.patch<Contact>(`/api/crm/contacts/${contactId}/`, contact);
  }

  async deleteContact(contactId: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/crm/contacts/${contactId}/`);
  }

  // ===================== Email Marketing =====================
  private _list<T>(resp: ApiResponse<any>): ApiResponse<T[]> {
    const data = Array.isArray(resp.data)
      ? resp.data
      : (resp.data && 'results' in resp.data ? resp.data.results : []);
    return { ...resp, data };
  }

  private async allEmailPages<T>(endpoint: string): Promise<ApiResponse<T[]>> {
    const rows: T[] = [];
    for (let page = 1; ; page++) {
      const separator = endpoint.includes('?') ? '&' : '?';
      const response = await this.get<T[] | { results: T[]; next: string | null }>(`${endpoint}${separator}page=${page}`);
      if (Array.isArray(response.data)) return { ...response, data: [...rows, ...response.data] };
      rows.push(...response.data.results);
      if (!response.data.next) return { ...response, data: rows };
      if (!response.data.results.length) throw new Error('Pagination returned an empty page before the end. Please retry.');
    }
  }

  async getEmailTemplates(): Promise<ApiResponse<EmailTemplate[]>> {
    return this.allEmailPages<EmailTemplate>('/api/marketing/email-templates/');
  }
  async getEmailTemplate(id: number): Promise<ApiResponse<EmailTemplate>> {
    return this.get<EmailTemplate>(`/api/marketing/email-templates/${id}/`);
  }
  async createEmailTemplate(data: Partial<EmailTemplate>): Promise<ApiResponse<EmailTemplate>> {
    return this.post<EmailTemplate>('/api/marketing/email-templates/', data);
  }
  async updateEmailTemplate(id: number, data: Partial<EmailTemplate>): Promise<ApiResponse<EmailTemplate>> {
    return this.patch<EmailTemplate>(`/api/marketing/email-templates/${id}/`, data);
  }
  async deleteEmailTemplate(id: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/marketing/email-templates/${id}/`);
  }
  async sendTestEmail(id: number, email?: string): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/email-templates/${id}/send-test/`, { email });
  }

  async getEmailCampaigns(): Promise<ApiResponse<EmailCampaign[]>> {
    return this.allEmailPages<EmailCampaign>('/api/marketing/email-campaigns/');
  }
  async getEmailCampaign(id: number): Promise<ApiResponse<EmailCampaign>> {
    return this.get<EmailCampaign>(`/api/marketing/email-campaigns/${id}/`);
  }
  async createEmailCampaign(data: Partial<EmailCampaign>): Promise<ApiResponse<EmailCampaign>> {
    return this.post<EmailCampaign>('/api/marketing/email-campaigns/', data);
  }
  async updateEmailCampaign(id: number, data: Partial<EmailCampaign>): Promise<ApiResponse<EmailCampaign>> {
    return this.patch<EmailCampaign>(`/api/marketing/email-campaigns/${id}/`, data);
  }
  async deleteEmailCampaign(id: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/marketing/email-campaigns/${id}/`);
  }
  async previewCampaignRecipients(id: number): Promise<ApiResponse<{ count: number; sample: Array<{ id: number; name: string; email: string }> }>> {
    return this.get(`/api/marketing/email-campaigns/${id}/recipients-preview/`);
  }
  async sendCampaignNow(id: number): Promise<ApiResponse<{ detail: string; limit_reached?: boolean }>> {
    return this.post(`/api/marketing/email-campaigns/${id}/send-now/`, {});
  }
  async scheduleCampaign(id: number, scheduled_at: string): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/email-campaigns/${id}/schedule/`, { scheduled_at });
  }
  async pauseCampaign(id: number): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/email-campaigns/${id}/pause/`, {});
  }
  async resumeCampaign(id: number): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/email-campaigns/${id}/resume/`, {});
  }
  async cancelCampaign(id: number): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/email-campaigns/${id}/cancel/`, {});
  }
  async getCampaignStats(id: number): Promise<ApiResponse<EmailCampaignStats>> {
    return this.get<EmailCampaignStats>(`/api/marketing/email-campaigns/${id}/stats/`);
  }

  async getDripCampaigns(): Promise<ApiResponse<DripCampaign[]>> {
    return this.allEmailPages<DripCampaign>('/api/marketing/drip-campaigns/');
  }
  async getDripCampaign(id: number): Promise<ApiResponse<DripCampaign>> {
    return this.get<DripCampaign>(`/api/marketing/drip-campaigns/${id}/`);
  }
  async createDripCampaign(data: Partial<DripCampaign>): Promise<ApiResponse<DripCampaign>> {
    return this.post<DripCampaign>('/api/marketing/drip-campaigns/', data);
  }
  async updateDripCampaign(id: number, data: Partial<DripCampaign>): Promise<ApiResponse<DripCampaign>> {
    return this.patch<DripCampaign>(`/api/marketing/drip-campaigns/${id}/`, data);
  }
  async deleteDripCampaign(id: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/marketing/drip-campaigns/${id}/`);
  }
  async activateDripCampaign(id: number): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/drip-campaigns/${id}/activate/`, {});
  }
  async deactivateDripCampaign(id: number): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/drip-campaigns/${id}/deactivate/`, {});
  }
  async enrollDripContacts(id: number, payload: { contact_ids?: number[]; tag_id?: number; contact_type?: string }): Promise<ApiResponse<{ detail: string; enrolled: number }>> {
    return this.post(`/api/marketing/drip-campaigns/${id}/enroll/`, payload);
  }
  async getDripEnrollments(id: number): Promise<ApiResponse<DripEnrollment[]>> {
    return this.allEmailPages<DripEnrollment>(`/api/marketing/drip-campaigns/${id}/enrollments/`);
  }

  async getContactDripEnrollments(contactId: number): Promise<ApiResponse<DripEnrollment[]>> {
    return this.allEmailPages<DripEnrollment>(`/api/marketing/drip-enrollments/?contact=${contactId}`);
  }

  async cancelDripEnrollment(enrollmentId: number): Promise<ApiResponse<{ detail: string }>> {
    return this.post(`/api/marketing/drip-enrollments/${enrollmentId}/cancel/`, {});
  }

  async getEmailQuota(): Promise<ApiResponse<EmailQuota>> {
    return this.get<EmailQuota>('/api/marketing/email/quota/');
  }

  // Organization Methods
  async uploadOrganizationLogo(file: File, fieldName: string = 'logo_wide_full_color'): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append(fieldName, file);

    // Get JWT access token
    const accessToken = authService.getAccessToken();
    
    const url = `${this.baseUrl}/api/organizations/current/`;
    
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(`HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).responseData = errorData;
        throw error;
      }

      const data = await response.json();

      return {
        data,
        status: response.status,
        message: data.message,
      };
    } catch (error) {
      throw error;
    }
  }

  // Pipeline data loader (similar to Kanban data loader)
  async getPipelineData(pipelineId: number): Promise<{
    pipelines: Pipeline[];
    stages: PipelineStage[];
    opportunities: Opportunity[];
  }> {
    const [pipelinesResponse, stagesResponse, opportunitiesResponse] = await Promise.all([
      this.getPipelines(),
      this.getPipelineStages(pipelineId),
      this.getOpportunities(pipelineId),
    ]);

    return {
      pipelines: pipelinesResponse.data,
      stages: stagesResponse.data,
      opportunities: opportunitiesResponse.data,
    };
  }

  // E-Signature Session Methods
  async getESignatureSession(sessionToken: string): Promise<ApiResponse<ESignatureSession>> {
    return this.get<ESignatureSession>(`/api/esignature-sessions/${sessionToken}/`);
  }

  async createESignatureSession(sessionData: ESignatureSessionFormData): Promise<ApiResponse<ESignatureSession>> {
    return this.post<ESignatureSession>('/api/esignature-sessions/', sessionData);
  }

  async updateESignatureSession(sessionToken: string, data: Partial<ESignatureSession>): Promise<ApiResponse<ESignatureSession>> {
    return this.patch<ESignatureSession>(`/api/esignature-sessions/${sessionToken}/`, data);
  }

  async submitSignature(submissionData: SignatureSubmissionData): Promise<ApiResponse<ESignatureSession>> {
    return this.post<ESignatureSession>('/api/esignature-sessions/complete/', submissionData);
  }

  async getContractPreview(contractId: number): Promise<ApiResponse<ContractPreviewData>> {
    return this.get<ContractPreviewData>(`/api/contracts/${contractId}/preview/`);
  }

  // Offline Signature Sync Methods
  async syncOfflineSignatures(): Promise<ApiResponse<{ synced: number; failed: number; errors: any[] }>> {
    const offlineSignatures = this.getOfflineSignatures();
    const results = { synced: 0, failed: 0, errors: [] as any[] };

    for (const signature of offlineSignatures) {
      try {
        await this.submitSignature({
          session_token: signature.session_token,
          signature_data: signature.signature_data,
          form_data: signature.form_data,
          consent_data: signature.consent_data,
          device_info: signature.device_info,
          location_data: signature.location_data,
        });

        results.synced++;
        this.removeOfflineSignature(signature.id);
      } catch (error) {
        results.failed++;
        results.errors.push({ signatureId: signature.id, error });
        
        // Update sync attempts
        signature.sync_attempts = (signature.sync_attempts || 0) + 1;
        signature.last_sync_attempt = new Date().toISOString();
        signature.sync_error = error instanceof Error ? error.message : 'Unknown error';
        
        this.updateOfflineSignature(signature);
      }
    }

    return {
      data: results,
      status: 200,
    };
  }

  // Offline Storage Management
  getOfflineSignatures(): OfflineSignature[] {
    try {
      const stored = localStorage.getItem('offline_signatures');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  saveOfflineSignature(signature: OfflineSignature): void {
    try {
      const signatures = this.getOfflineSignatures();
      signatures.push(signature);
      localStorage.setItem('offline_signatures', JSON.stringify(signatures));
    } catch (error) {
      // Failed to save offline signature
    }
  }

  updateOfflineSignature(updatedSignature: OfflineSignature): void {
    try {
      const signatures = this.getOfflineSignatures();
      const index = signatures.findIndex(s => s.id === updatedSignature.id);
      if (index !== -1) {
        signatures[index] = updatedSignature;
        localStorage.setItem('offline_signatures', JSON.stringify(signatures));
      }
    } catch (error) {
      // Failed to update offline signature
    }
  }

  removeOfflineSignature(signatureId: string): void {
    try {
      const signatures = this.getOfflineSignatures();
      const filtered = signatures.filter(s => s.id !== signatureId);
      localStorage.setItem('offline_signatures', JSON.stringify(filtered));
    } catch (error) {
      // Failed to remove offline signature
    }
  }

  // AI Content Writer Methods
  async getWebsitePagesForImport(projectId?: number): Promise<ApiResponse<WebsitePagesForImportResponse>> {
    const qs = projectId ? `?project_id=${projectId}` : '';
    return this.get<WebsitePagesForImportResponse>(`/api/marketing/topical-maps/website-pages/${qs}`);
  }

  async importWebsitePages(data: { project_id: number; page_ids: number[] }): Promise<ApiResponse<ImportFromWebsiteResponse>> {
    return this.post<ImportFromWebsiteResponse>('/api/marketing/topical-maps/import-from-website/', data);
  }

  async getTopicalMaps(filters?: { status?: string; top_page?: number }): Promise<ApiResponse<any[]>> {
    let endpoint = '/api/marketing/topical-maps/';
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.top_page) params.append('top_page', filters.top_page.toString());
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    return this.get<any[]>(endpoint);
  }

  async createTopicalMap(data: { top_page: number }): Promise<ApiResponse<any>> {
    return this.post<any>('/api/marketing/topical-maps/', data);
  }

  async generateTopicalMapIdeas(topicalMapId: number): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/marketing/topical-maps/${topicalMapId}/generate_ideas/`, {});
  }

  async approveTopicalMap(topicalMapId: number): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/marketing/topical-maps/${topicalMapId}/approve/`, {});
  }

  async getBlogIdeas(topicalMapId: number, filters?: { used?: boolean; search?: string }): Promise<ApiResponse<any[]>> {
    let endpoint = `/api/marketing/blog-ideas/?topical_map=${topicalMapId}`;
    
    if (filters?.used !== undefined) {
      endpoint += `&used=${filters.used}`;
    }
    if (filters?.search) {
      endpoint += `&search=${encodeURIComponent(filters.search)}`;
    }
    
    return this.get<any[]>(endpoint);
  }

  async updateBlogIdea(id: number, data: any): Promise<ApiResponse<any>> {
    return this.patch<any>(`/api/marketing/blog-ideas/${id}/`, data);
  }

  async markBlogIdeaAsUsed(id: number): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/marketing/blog-ideas/${id}/mark_used/`, {});
  }

  async bulkUpdateBlogIdeas(ids: number[], data: any): Promise<ApiResponse<any>> {
    return this.post<any>('/api/marketing/blog-ideas/bulk_update/', { ids, ...data });
  }

  async bulkDeleteBlogIdeas(ids: number[]): Promise<ApiResponse<any>> {
    return this.post<any>('/api/marketing/blog-ideas/bulk_delete/', { ids });
  }

  async getArticles(filters?: { status?: string; in_progress?: boolean }): Promise<ApiResponse<any[]>> {
    let endpoint = '/api/marketing/articles/';
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.in_progress) params.append('in_progress', 'true');
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    return this.get<any[]>(endpoint);
  }

  async generateArticle(data: { blog_idea_id?: number; topical_map_id?: number; limit?: number }): Promise<ApiResponse<any>> {
    return this.post<any>('/api/articles/generate/', data);
  }

  async generateBulkArticles(blogIdeaIds: number[]): Promise<ApiResponse<any>> {
    return this.post<any>('/api/articles/generate_bulk/', { blog_idea_ids: blogIdeaIds });
  }

  async getArticleProgress(articleId: number): Promise<ApiResponse<any>> {
    return this.get<any>(`/api/marketing/articles/${articleId}/progress/`);
  }

  async getGenerationProgress(): Promise<ApiResponse<any[]>> {
    return this.get<any[]>('/api/articles/generation-progress/');
  }

  async previewArticle(articleId: number): Promise<ApiResponse<any>> {
    return this.get<any>(`/api/marketing/articles/${articleId}/preview/`);
  }

  async publishArticle(articleId: number): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/marketing/articles/${articleId}/publish/`, {});
  }

  async retryArticleGeneration(articleId: number): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/marketing/articles/${articleId}/retry_generation/`, {});
  }

  async saveArticleRevision(articleId: number): Promise<ApiResponse<any>> {
    return this.post<any>(`/api/marketing/articles/${articleId}/save_revision/`, {});
  }

  async getContentBlocks(articleId: number): Promise<ApiResponse<any[]>> {
    return this.get<any[]>(`/api/content-blocks/?article=${articleId}`);
  }

  async updateContentBlock(id: number, data: any): Promise<ApiResponse<any>> {
    return this.patch<any>(`/api/content-blocks/${id}/`, data);
  }

  async deleteContentBlock(id: number): Promise<ApiResponse<void>> {
    return this.delete<void>(`/api/content-blocks/${id}/`);
  }

  async createContentBlock(data: any): Promise<ApiResponse<any>> {
    return this.post<any>('/api/content-blocks/', data);
  }

  async reorderContentBlocks(articleId: number, blocks: { id: number; order: number }[]): Promise<ApiResponse<any>> {
    return this.post<any>('/api/content-blocks/reorder/', { article_id: articleId, blocks });
  }

  async getContentBlockRevisions(articleId: number): Promise<ApiResponse<any[]>> {
    return this.get<any[]>(`/api/content-blocks/revisions/?article=${articleId}`);
  }

  async getContentStats(): Promise<ApiResponse<any>> {
    return this.get<any>('/api/local-seo/stats/');
  }

  // Auto-sync functionality
  async autoSyncOfflineSignatures(): Promise<void> {
    if (!navigator.onLine) return;

    const offlineSignatures = this.getOfflineSignatures();
    if (offlineSignatures.length === 0) return;

    try {
      const result = await this.syncOfflineSignatures();
      
      if (result.data.synced > 0) {
        // Dispatch custom event for UI updates
        window.dispatchEvent(new CustomEvent('offlineSignaturesSynced', {
          detail: result.data
        }));
      }
    } catch (error) {
      // Auto-sync failed
    }
  }

  // Initialize auto-sync listeners
  initializeOfflineSync(): void {
    // Sync when coming back online
    window.addEventListener('online', () => {
      setTimeout(() => this.autoSyncOfflineSignatures(), 1000);
    });

    // Periodic sync (every 5 minutes when online)
    setInterval(() => {
      if (navigator.onLine) {
        this.autoSyncOfflineSignatures();
      }
    }, 5 * 60 * 1000);

    // Initial sync on page load
    if (navigator.onLine) {
      setTimeout(() => this.autoSyncOfflineSignatures(), 2000);
    }
  }

  // Device and compliance helpers
  async captureDeviceInfo(): Promise<any> {
    const deviceInfo = {
      platform: navigator.platform,
      browser: this.getBrowserInfo(),
      version: navigator.appVersion,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      touch_capable: 'ontouchstart' in window,
      stylus_capable: 'PointerEvent' in window,
      user_agent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
    };

    return deviceInfo;
  }

  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    if (ua.includes('Opera')) return 'Opera';
    
    return 'Unknown';
  }

  async captureLocationData(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString(),
          });
        },
        (error) => {
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }

  // Weather Report generation (async: kick off → poll status → finalize)
  async generateWeatherReport(
    opportunityId: number,
    config: {
      title?: string;
      type?: string;
      location?: string;
      analysis_period?: '6_months' | '9_months' | '24_months';
    } = {}
  ): Promise<ApiResponse<{
    report_id: string;
    document_id: number;
    status: string;
    message: string;
  }>> {
    const title = config.title || 'Weather Report';
    const kickoff = await this.post<{
      report_id: string;
      status: string;
      title?: string;
    }>(`/api/integrations/opportunities/${opportunityId}/weather-report/`, {
      title,
      type: config.type || 'weather_summary',
      location: config.location || '',
      analysis_period: config.analysis_period || '6_months',
      report_type: 'address',
      template: 'address_report',
      include_charts: false,
      include_forecast: false,
      include_storm_events: true,
      generate_pdf: true,
      branding: true,
      color_scheme: 'address_report',
    });

    const reportId = kickoff.data?.report_id;
    if (!reportId) {
      throw new Error('Reports service did not return a report_id');
    }

    const pollIntervalMs = 3000;
    const maxAttempts = 100;
    let completed = false;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

      try {
        const statusResponse = await this.get<{
          status?: string;
          error_message?: string;
        }>(`/api/integrations/reports/${reportId}/status/`);

        const reportStatus = (statusResponse.data?.status || '').toLowerCase();
        if (reportStatus === 'completed') {
          completed = true;
          break;
        }
        if (reportStatus === 'failed') {
          throw new Error(
            statusResponse.data?.error_message || 'Report generation failed'
          );
        }
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message === 'Report generation failed' ||
            error.message.includes('Reports service'))
        ) {
          throw error;
        }
        // Transient poll errors — keep trying until timeout.
      }
    }

    if (!completed) {
      throw new Error('Report generation timed out. Please try again.');
    }

    const finalize = await this.post<{
      report_id: string;
      document_id: number;
      status: string;
      message: string;
    }>(
      `/api/integrations/opportunities/${opportunityId}/weather-report/${reportId}/finalize/`,
      { title }
    );

    if (!finalize.data?.document_id) {
      throw new Error('Report completed but no document was created');
    }

    return finalize;
  }

  // Compliance logging
  async logComplianceEvent(
    sessionToken: string, 
    eventType: string, 
    details: Record<string, any>
  ): Promise<void> {
    try {
      await this.post(`/api/esignature-sessions/${sessionToken}/compliance-log/`, {
        event_type: eventType,
        details,
        timestamp: new Date().toISOString(),
        ip_address: await this.getClientIP(),
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      // Failed to log compliance event
    }
  }

  private async getClientIP(): Promise<string | null> {
    try {
      // In a real implementation, you might call an IP detection service
      // For now, return null to let the backend capture it
      return null;
    } catch (error) {
      return null;
    }
  }

  // ===================== Website Builder =====================
  // Typed client for /api/website-builder/projects/ — response shapes come
  // from backend/apexos/apps/website_builder/serializers.py and views.py.
  websiteBuilder = {
    listProjects: async (): Promise<ApiResponse<WebsiteProject[]>> => {
      const resp = await this.get<WebsiteProject[] | { count: number; results: WebsiteProject[] }>(
        '/api/website-builder/projects/'
      );
      return this._list<WebsiteProject>(resp);
    },

    createProject: (data: WebsiteProjectCreatePayload): Promise<ApiResponse<WebsiteProject>> =>
      this.post<WebsiteProject>('/api/website-builder/projects/', data),

    getProject: (id: number | string): Promise<ApiResponse<WebsiteProjectDetail>> =>
      this.get<WebsiteProjectDetail>(`/api/website-builder/projects/${id}/`),

    updateProject: (
      id: number | string,
      data: WebsiteProjectUpdatePayload
    ): Promise<ApiResponse<WebsiteProjectDetail>> =>
      this.patch<WebsiteProjectDetail>(`/api/website-builder/projects/${id}/`, data),

    deleteProject: (id: number | string): Promise<ApiResponse<void>> =>
      this.delete<void>(`/api/website-builder/projects/${id}/`),

    getStatus: (id: number | string): Promise<ApiResponse<WebsiteProjectStatus>> =>
      this.get<WebsiteProjectStatus>(`/api/website-builder/projects/${id}/status/`),

    /** Connect a custom domain — returns the DNS records the customer must create. */
    setCustomDomain: (
      id: number | string,
      domain: string
    ): Promise<ApiResponse<{ status: string; custom_domain: string; dns_records: DnsRecord[] }>> =>
      this.post(`/api/website-builder/projects/${id}/domain/`, { domain }),

    removeCustomDomain: (id: number | string): Promise<ApiResponse<{ status: string }>> =>
      this.delete(`/api/website-builder/projects/${id}/domain/`),

    /** Re-check certificate/DNS state now (the background sweep also runs every 10 min). */
    checkCustomDomain: (
      id: number | string
    ): Promise<ApiResponse<{ status: string; custom_domain: string; dns_records: DnsRecord[]; error: string }>> =>
      this.post(`/api/website-builder/projects/${id}/check-domain/`, {}),

    /** Record consent to a legal page's content (unlocks publishing). */
    approveLegalPage: (
      id: number | string,
      pageId: number | string
    ): Promise<ApiResponse<{ status: string; approved_at: string; approved_by: string }>> =>
      this.post(`/api/website-builder/projects/${id}/pages/${pageId}/approve-legal/`, {}),

    /** Generate 10-15 page-relevant FAQs as FAQPage JSON-LD (background AI call). */
    generatePageFaqs: (
      id: number | string,
      pageId: number | string
    ): Promise<ApiResponse<WebsiteTaskDispatchResponse>> =>
      this.post(`/api/website-builder/projects/${id}/pages/${pageId}/generate-faqs/`, {}),

    /**
     * Kick off homepage generation. When a design system already exists only
     * the cheap homepage-content call re-runs; pass regenerateDesign to redo
     * the entire design system (templates + chrome). 409 when already generating.
     */
    generateHomepage: (
      id: number | string,
      opts?: { regenerateDesign?: boolean }
    ): Promise<ApiResponse<WebsiteTaskDispatchResponse>> =>
      this.post<WebsiteTaskDispatchResponse>(
        `/api/website-builder/projects/${id}/generate-homepage/`,
        opts?.regenerateDesign ? { regenerate_design: true } : {}
      ),

    generateSite: (id: number | string): Promise<ApiResponse<WebsiteTaskDispatchResponse>> =>
      this.post<WebsiteTaskDispatchResponse>(`/api/website-builder/projects/${id}/generate-site/`, {}),

    deploy: (id: number | string): Promise<ApiResponse<WebsiteTaskDispatchResponse>> =>
      this.post<WebsiteTaskDispatchResponse>(`/api/website-builder/projects/${id}/deploy/`, {}),

    revise: (
      id: number | string,
      payload: { feedback: string; page_id?: number | null }
    ): Promise<ApiResponse<WebsiteTaskDispatchResponse>> =>
      this.post<WebsiteTaskDispatchResponse>(`/api/website-builder/projects/${id}/revise/`, payload),

    getPages: (id: number | string): Promise<ApiResponse<WebsitePageSummary[]>> =>
      this.get<WebsitePageSummary[]>(`/api/website-builder/projects/${id}/pages/`),

    getPage: (id: number | string, pageId: number | string): Promise<ApiResponse<WebsitePageDetail>> =>
      this.get<WebsitePageDetail>(`/api/website-builder/projects/${id}/pages/${pageId}/`),

    createPage: (
      id: number | string,
      data: WebsitePageCreatePayload
    ): Promise<ApiResponse<WebsitePageCreateResponse>> =>
      this.post<WebsitePageCreateResponse>(`/api/website-builder/projects/${id}/pages/`, data),

    updatePage: (
      id: number | string,
      pageId: number | string,
      data: WebsitePageUpdatePayload
    ): Promise<ApiResponse<WebsitePageUpdateResponse>> =>
      this.patch<WebsitePageUpdateResponse>(`/api/website-builder/projects/${id}/pages/${pageId}/update/`, data),

    generatePage: (
      id: number | string,
      pageId: number | string
    ): Promise<ApiResponse<{ status: string; page_id: number; title: string }>> =>
      this.post(`/api/website-builder/projects/${id}/pages/${pageId}/generate/`, {}),

    generateRemaining: (
      id: number | string
    ): Promise<ApiResponse<{ status: string; pages_queued: number }>> =>
      this.post(`/api/website-builder/projects/${id}/generate-remaining/`, {}),

    refreshPreview: (
      id: number | string
    ): Promise<ApiResponse<{ preview_url: string; pages: WebsitePageSummary[] }>> =>
      this.post(`/api/website-builder/projects/${id}/refresh-preview/`, {}),

    /**
     * Publish ONE page's current content to the live site (fast path for
     * post-launch edits). 400 unless the site is fully deployed.
     */
    deployPage: (
      id: number | string,
      pageId: number | string
    ): Promise<ApiResponse<WebsiteTaskDispatchResponse & { page_id: number }>> =>
      this.post(`/api/website-builder/projects/${id}/pages/${pageId}/deploy/`, {}),

    /**
     * AI-edit ONE page in plain English ("shorten the intro, mention our
     * 24/7 line"). Edits the page's structured content and rebuilds it —
     * runs async; poll the page detail until updated_at changes.
     */
    revisePage: (
      id: number | string,
      pageId: number | string,
      feedback: string
    ): Promise<ApiResponse<WebsiteTaskDispatchResponse>> =>
      this.post(`/api/website-builder/projects/${id}/pages/${pageId}/revise/`, { feedback }),

    /**
     * Scope-routed AI edit: one instruction that can change the page's
     * content, the shared header/footer, the page layout, or business data
     * (phone/CTA) — the backend classifies it. Returns a revision_id to
     * poll via getRevision until status is terminal.
     */
    aiEdit: (
      id: number | string,
      payload: { feedback: string; page_id?: number | null; force?: boolean }
    ): Promise<ApiResponse<WebsiteAiEditDispatchResponse>> =>
      this.post<WebsiteAiEditDispatchResponse>(`/api/website-builder/projects/${id}/ai-edit/`, payload),

    /** Poll one revision's status (pending/running/succeeded/failed/no_change). */
    getRevision: (
      id: number | string,
      revisionId: number | string
    ): Promise<ApiResponse<WebsiteRevisionInfo>> =>
      this.get<WebsiteRevisionInfo>(`/api/website-builder/projects/${id}/revisions/${revisionId}/`),

    /** Visual editor: the page's draft rendered as an annotated document. */
    editorDocument: (
      id: number | string,
      pageId: number | string
    ): Promise<ApiResponse<EditorDocumentResponse>> =>
      this.get<EditorDocumentResponse>(
        `/api/website-builder/projects/${id}/pages/${pageId}/editor-document/`
      ),

    /**
     * Visual editor save: serialized field edits -> content_json through the
     * assembler. 409 codes: stale_page, manual_edits_present (force-able),
     * generation_in_progress; 400 carries {details: string[]}.
     */
    visualEdit: (
      id: number | string,
      pageId: number | string,
      payload: VisualEditPayload
    ): Promise<ApiResponse<VisualEditResponse>> =>
      this.post<VisualEditResponse>(
        `/api/website-builder/projects/${id}/pages/${pageId}/visual-edit/`,
        payload
      ),

    /** Revision history (project-wide; entries carry page + page_title). */
    listRevisions: (
      id: number | string
    ): Promise<ApiResponse<WebsiteRevisionInfo[]>> =>
      this.get(`/api/website-builder/projects/${id}/revisions/`),

    /**
     * Propagate edited header/footer/hero to every page (zero AI calls).
     * Pass redeploy to also push a live site. 400 for non-template projects,
     * 409 while generation is in progress.
     */
    republishChrome: (
      id: number | string,
      opts?: { redeploy?: boolean }
    ): Promise<ApiResponse<WebsiteTaskDispatchResponse>> =>
      this.post<WebsiteTaskDispatchResponse>(
        `/api/website-builder/projects/${id}/republish-chrome/`,
        { redeploy: !!opts?.redeploy }
      ),

    /** Pre-flight org-data check; each failed check carries a fix_url. */
    readiness: (): Promise<ApiResponse<ReadinessResponse>> =>
      this.get<ReadinessResponse>('/api/website-builder/projects/readiness/'),

    styleOptions: (): Promise<ApiResponse<WebsiteStyleOptions>> =>
      this.get<WebsiteStyleOptions>('/api/website-builder/projects/style-options/'),

    componentOptions: (id: number | string): Promise<ApiResponse<WebsiteComponentOptions>> =>
      this.get<WebsiteComponentOptions>(`/api/website-builder/projects/${id}/component-options/`),

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    knowledgeGraph: (): Promise<ApiResponse<Record<string, any>>> =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.get<Record<string, any>>('/api/website-builder/projects/knowledge-graph/'),
  };

  // ===================== QuickBooks Online =====================
  // Typed client for /api/quickbooks/ — response shapes come from
  // backend/apexos/apps/quickbooks/serializers.py and views.py.
  quickbooks = {
    /** Connection, settings and unresolved-item counts in one call. */
    getStatus: (): Promise<ApiResponse<QuickBooksStatus>> =>
      this.get<QuickBooksStatus>('/api/quickbooks/status/'),

    /**
     * Start the Intuit consent flow. The backend stores the chosen starting
     * point before handing back a URL, because the callback has no way to ask
     * again once the admin is on Intuit's side.
     */
    connect: (backfillMode: BackfillMode): Promise<ApiResponse<QuickBooksConnectResponse>> =>
      this.get<QuickBooksConnectResponse>(
        `/api/quickbooks/oauth/initiate/?backfill_mode=${encodeURIComponent(backfillMode)}`
      ),

    /** The bookkeeper's account/item mapping. Served separately from status. */
    getSettings: (): Promise<ApiResponse<QuickBooksSyncSettings>> =>
      this.get<QuickBooksSyncSettings>('/api/quickbooks/settings/'),

    /** Revoke the tokens. Records already in QuickBooks stay there. */
    disconnect: (): Promise<ApiResponse<QuickBooksDisconnectResponse>> =>
      this.post<QuickBooksDisconnectResponse>('/api/quickbooks/disconnect/', {}),

    /** Accounts and items read live from the connected company, for the pickers. */
    getReferenceData: (): Promise<ApiResponse<QuickBooksReferenceData>> =>
      this.get<QuickBooksReferenceData>('/api/quickbooks/reference-data/'),

    updateSettings: (
      data: QuickBooksSyncSettingsUpdate
    ): Promise<ApiResponse<QuickBooksSyncSettings>> =>
      this.patch<QuickBooksSyncSettings>('/api/quickbooks/settings/', data),

    /** Queue an immediate drain of everything pending or failed. */
    syncNow: (): Promise<ApiResponse<QuickBooksSyncNowResponse>> =>
      this.post<QuickBooksSyncNowResponse>('/api/quickbooks/sync-now/', {}),

    /** Sync activity. Pass a status to narrow to just the failures. */
    getLogs: async (params?: {
      status?: QuickBooksLogFilter;
      limit?: number;
    }): Promise<ApiResponse<QuickBooksSyncLog[]>> => {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.limit) query.set('limit', String(params.limit));
      const suffix = query.toString() ? `?${query.toString()}` : '';
      const resp = await this.get<any>(`/api/quickbooks/logs/${suffix}`);
      return this._list<QuickBooksSyncLog>(resp);
    },

    retryLog: (id: number | string): Promise<ApiResponse<QuickBooksRetryResponse>> =>
      this.post<QuickBooksRetryResponse>(`/api/quickbooks/logs/${id}/retry/`, {}),

    /**
     * Release one held invoice to QuickBooks.
     *
     * Only reachable in "hold for review" mode: held rows are the one thing the
     * background sync deliberately leaves alone, so this is the only way they move.
     */
    approveLog: (id: number | string): Promise<ApiResponse<QuickBooksRetryResponse>> =>
      this.post<QuickBooksRetryResponse>(`/api/quickbooks/logs/${id}/approve/`, {}),
  };

  // Chat attachment upload
  async uploadChatAttachment(file: File): Promise<ApiResponse<{
    url: string;
    name: string;
    type: string;
    size: number;
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    // Get JWT access token
    const accessToken = authService.getAccessToken();

    const url = `${this.baseUrl}/api/teamchat/attachments/`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(errorData?.error || `HTTP error! status: ${response.status}`);
        (error as any).responseData = errorData;
        throw error;
      }

      const data = await response.json();

      return {
        data,
        status: response.status,
        message: data.message,
      };
    } catch (error) {
      throw error;
    }
  }
}

export const apiClient = new ApiClient();

// Create auth API using cookie-based endpoints
export const authApi = {
  /**
   * Login using cookie-based endpoint
   * Refresh token is stored in httpOnly cookie, access token returned in body
   */
  async login(username: string, password: string, replacement?: SessionReplacement) {
    const response = await fetch(`${API_BASE_URL}${replacement ? '/api/auth/sessions/replace/' : '/api/auth/login/'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for Set-Cookie header
      body: JSON.stringify(replacement ?? { username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw loginError(error);
    }

    const data = await response.json();

    // Store access token in memory (refresh token is in httpOnly cookie)
    authService.setAccessToken(data.access);

    return data;
  },

  async requestPasswordReset(email: string) {
    const response = await fetch(`${API_BASE_URL}/api/users/request-password-reset/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      // Handle different error response formats
      const errorMessage = error.error || 
                          error.detail || 
                          error.message ||
                          (error.errors && typeof error.errors === 'object' 
                            ? Object.values(error.errors).flat().join(', ')
                            : null) ||
                          'Password reset request failed';
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Logout using cookie-based endpoint
   * Clears httpOnly cookie and blacklists refresh token
   */
  async logout() {
    const response = await fetch(`${API_BASE_URL}/api/auth/logout/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for deletion
    });

    if (!response.ok) {
      console.warn('Logout request failed, but clearing local tokens anyway');
    }

    // Clear access token from memory
    authService.clearAccessToken();
  },

  async resetPassword(token: string, newPassword: string) {
    const response = await fetch(`${API_BASE_URL}/api/users/reset-password/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || error.error || 'Password reset failed');
    }

    return response.json();
  },

  /**
   * Check if user has an existing session (refresh token cookie)
   */
  async checkAuthStatus() {
    return authService.checkAuthStatus();
  },

  /**
   * Initialize authentication on page load
   * Refreshes access token if user has a valid session
   */
  async initializeAuth() {
    return authService.initializeAuth();
  },
};

// ─── Support API ─────────────────────────────────────────────────────────────
export const supportApi = {
  async getSupportTickets(params?: Record<string, string>) {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${qs}`, {
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch tickets');
    return response.json() as Promise<{ results: SupportTicket[]; count: number; next?: string | null; previous?: string | null }>;
  },

  async getSupportTicket(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${id}/`, {
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch ticket');
    return response.json() as Promise<SupportTicket>;
  },

  async createSupportTicket(data: CreateTicketPayload) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create ticket');
    return response.json() as Promise<SupportTicket>;
  },

  async getTicketMessages(ticketId: string) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/messages/`, {
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json() as Promise<TicketMessage[]>;
  },

  async createTicketMessage(ticketId: string, data: CreateMessagePayload) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/messages/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json() as Promise<TicketMessage>;
  },

  async closeTicket(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${id}/close/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to close ticket');
    return response.json() as Promise<SupportTicket>;
  },

  async reopenTicket(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${id}/reopen/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to reopen ticket');
    return response.json() as Promise<SupportTicket>;
  },

  async resolveTicket(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${id}/resolve/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to resolve ticket');
    return response.json() as Promise<SupportTicket>;
  },

  async assignTicket(id: string, userId: number | null) {
    const response = await fetch(`${API_BASE_URL}/api/support/tickets/${id}/assign/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
      body: JSON.stringify({ user_id: userId }),
    });
    if (!response.ok) throw new Error('Failed to assign ticket');
    return response.json() as Promise<SupportTicket>;
  },

  async sendAIChatMessage(message: string, conversationHistory: AIChatMessage[], conversationId?: string | null) {
    const response = await fetch(`${API_BASE_URL}/api/support/ai-chat/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authService.getAccessToken()}`,
      },
      body: JSON.stringify({
        message,
        conversation_history: conversationHistory,
        ...(conversationId ? { conversation_id: conversationId } : {}),
      }),
    });
    if (!response.ok) throw new Error('AI chat request failed');
    return response.json() as Promise<{ response: string; conversation_id: string }>;
  },

  async getAIConversations() {
    const response = await fetch(`${API_BASE_URL}/api/support/ai-conversations/`, {
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch AI conversations');
    return response.json() as Promise<{ results: AIConversation[]; count: number }>;
  },

  async getAIConversation(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/support/ai-conversations/${id}/`, {
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch AI conversation');
    return response.json() as Promise<AIConversation>;
  },

  async getActiveAIConversation() {
    const response = await fetch(`${API_BASE_URL}/api/support/ai-conversations/active/`, {
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (response.status === 204) return null;
    if (!response.ok) throw new Error('Failed to fetch active AI conversation');
    return response.json() as Promise<AIConversation>;
  },

  async deleteAIConversation(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/support/ai-conversations/${id}/`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authService.getAccessToken()}` },
    });
    if (!response.ok) throw new Error('Failed to delete AI conversation');
  },
};

/**
 * Fetch a binary endpoint (e.g. email attachments) with auth and return the
 * Blob. The JSON ApiClient can't carry blobs, so this mirrors its auth
 * headers directly. Also used for inline cid: images and attachment previews.
 */
export class ApiBlobError extends Error {
  status: number;
  retryAfter: number | null;

  constructor(message: string, status: number, retryAfter: number | null) {
    super(message);
    this.status = status;
    this.retryAfter = retryAfter;
    this.name = 'ApiBlobError';
  }
}

export async function fetchApiBlob(endpoint: string, options: { signal?: AbortSignal } = {}): Promise<Blob> {
  const url = new URL(endpoint, API_BASE_URL);
  if (url.origin !== API_BASE_URL || !url.pathname.startsWith('/api/')) {
    throw new Error('Authenticated downloads require an application API URL.');
  }
  const request = () => {
    options.signal?.throwIfAborted();
    const accessToken = authService.getAccessToken();
    const impersonatedUserId = getImpersonatedUserId();
    return fetch(url.href, {
      signal: options.signal,
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        ...(impersonatedUserId && { 'X-Impersonate-User-Id': String(impersonatedUserId) }),
      },
      credentials: 'include',
    });
  };
  let response = await request();
  if (response.status === 401) {
    try {
      await authService.refreshAccessToken();
    } catch {
      if (!(await authService.initializeAuth())) throw new Error('Sign in again to download this document.');
    }
    response = await request();
  }
  if (!response.ok) {
    let detail = `Download failed (${response.status})`;
    try {
      const data = await response.json();
      if (data?.error) detail = data.error;
    } catch { /* non-JSON error body */ }
    throw new ApiBlobError(detail, response.status, retryAfterMs(response.headers.get('Retry-After')));
  }
  return response.blob();
}

/** Download a binary endpoint with auth and trigger a browser save. */
export async function downloadApiBlob(endpoint: string, filename: string): Promise<void> {
  const blob = await fetchApiBlob(endpoint);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// Create default api export for backward compatibility
export const api = apiClient;

// Default export for convenience
export default api;
